import { format, isValid, type Locale, parseISO } from 'date-fns'
import { enUS, it } from 'date-fns/locale'
import { formatInTimeZone } from 'date-fns-tz'
import i18next, { DEFAULT_LANGUAGE } from '@/translations/i18n.tsx'

const locales = {
	en: enUS,
	it
} as const satisfies Record<string, Locale>

export const getLocale = (languageCode: string): Locale => {
	return (
		locales[languageCode as keyof typeof locales] ||
		locales[DEFAULT_LANGUAGE.code]
	)
}

/**
 * Parses a date the way the app means it, which `new Date(string)` does not.
 *
 * ECMAScript reads a date-only string (`'2026-03-02'`) as **UTC** midnight but a
 * date-time one (`'2026-03-02T00:00:00'`) as **local** midnight. Calendar dates
 * here — an event's `date`, a roadmap's `dateFrom`, a filter's `dateTo` — carry
 * no zone and mean a day on the viewer's calendar, so reading them as UTC moves
 * them a day back for anyone west of it: `'2026-03-02'` becomes Sunday
 * March 1st, 16:00, in Los Angeles. `parseISO` reads them as local midnight,
 * which is what dayjs did and what every consumer here assumes.
 *
 * Full instants (`'…T…Z'` or with an offset) are unaffected: their zone is
 * stated, so both parsers agree, and `parseISO` keeps them absolute.
 */
export const parseDate = (value: string | Date): Date =>
	value instanceof Date ? value : parseISO(value)

export const formatLocaleDate = (date: string | Date, formatType = 'PP') => {
	const currentLang = i18next.language || DEFAULT_LANGUAGE.code
	const locale =
		locales[currentLang as keyof typeof locales] ||
		locales[DEFAULT_LANGUAGE.code]
	return format(parseDate(date), formatType, { locale })
}

/**
 * Formats an absolute instant for display, in `timeZone` when one is given.
 *
 * The zone is what makes an event timestamp read the same for everyone. The
 * instant is absolute, so without it the viewer's browser decides the clock, and
 * a team spread across countries reads a different time than the market did.
 * Pass the event's `time_zone` for anything that happened at an event: arrival
 * times, close instants, capture and export provenance.
 *
 * Not for naive wall-clock values (`'2026-07-09T18:00:00'`, no offset): those
 * are already local to the market, and converting them would move the hour. The
 * planned start/end times are the only such values; `formatStartTime` in the
 * marquee utils handles them.
 */
export const formatDate = (
	date: string | Date | undefined | null,
	formatType = 'yyyy-MM-dd',
	timeZone?: string | null
) => {
	if (!date) {
		return '-'
	}
	const parsed = parseDate(date)
	if (!isValid(parsed)) {
		return '-'
	}
	return timeZone
		? formatInTimeZone(parsed, timeZone, formatType)
		: format(parsed, formatType)
}
